/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

import Cleaner from '../cleaner/cleaner';
import ErrorName from '../../core/error/error-name';
import FileSystemUtil from '../file/system/file-system-util';

// TODO #84 Přerušení stahování.
// - Downloader#cancel()
// - Zruší probíhající stahování.
// - Zde by se mělo pokusit přerušit aktuální stahování. Každopádně přerušit sérii.
// - Neměl by být vstupem downloadPromise, pokud by bylo v budoucnu více paralelních stahování?
// - Možná inspirace rozhraním $timeout: https://docs.angularjs.org/api/ng/service/$timeout
// TODO Přejmenovat? AssetReplacer, ContentDownloader
/**
 * Stahovač obsahu
 *
 * ![Diagram tříd](./core/downloader/downloader-class.doc.png)
 *
 * ![Diagram aktivit](./core/downloader/downloader-activity.doc.png)
 * @implements {EventBus}
 */
class Downloader {
  /**
   * @param {AssetDownloader} assetDownloader
   * @param {BasePathResolver} basePathResolver
   * @param {Object} config Konfigurace aplikace.
   * @param {FileSystemSpaceDetector} fileSystemSpaceDetector
   * @param {FileSystemManager} fileSystemManager
   * @param {FileSystemUnitFinder} fileSystemUnitFinder
   * @param {Logger} logger
   * @param {LogicalUnitMapper} logicalUnitMapper
   * @param {Store} primaryStore Hlavní datový sklad ve kterém jsou uloženy soubory a přehrávaný obsah.
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   */
  constructor(
    assetDownloader,
    basePathResolver,
    config,
    fileSystemSpaceDetector,
    fileSystemManager,
    fileSystemUnitFinder,
    logger,
    logicalUnitMapper,
    primaryStore,
    session,
    viewerRegistry
  ) {
    this.assetDownloader = assetDownloader;
    const cleaner = new Cleaner(
      assetDownloader,
      basePathResolver,
      primaryStore,
      session
    );
    this.cleaner = cleaner;
    this.defaultSize = config.device.downloader.defaultSize;
    this.fileSystemSpaceDetector = fileSystemSpaceDetector;
    this.logger = logger;
    this.viewerRegistry = viewerRegistry;

    this.basePathResolver = basePathResolver;
    this.fileSystemManager = fileSystemManager;
    this.fileSystemUnitFinder = fileSystemUnitFinder;
    this.logicalUnitMapper = logicalUnitMapper;

    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.primaryStore = primaryStore;
    // Navázání naslouchání na instanci.
    this.onContent = this.onContent.bind(this);
    this.onError = this.onError.bind(this);
    // Naslouchání událostem.
    cleaner.on('content', this.onContent);
    cleaner.on('error', this.onError);
  }

  /**
   * Uvolní všechny zdroje pro garbage collection.
   * @returns {undefined} Nic.
   */
  destroy() {
    // TODO #84 Přerušení stahování.
    // - Půjde synchronně? Asi ne.
    // - Minimálně volat cancel.
    const { cleaner } = this;
    cleaner.off('content', this.onContent);
    cleaner.off('error', this.onError);
  }

  /**
   * Stáhne soubory z nového obsahu.
   * Při paralelním stahování více obsahů, může dojít k problémům
   * v synchronizaci, např. paralelní stahování stejného souboru
   * do stejného umístění.
   * Proto se doporučuje tuto metudo vždy volat sériově.
   * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise.<Array.<Asset>>} Seznam stažených assetů.
   */
  download(contentInfo) {
    // TODO #84 Zvážit, jak signalizovat přerušení stahování:
    // - Místo assetIdArray vracet null.
    //   - Ignorovat rozestahované soubory?
    // - Vracet assetIdArray a příznak přerušení.
    //   - Rodič rozhodne co se soubory při přerušení.
    // TODO Šlo by přepsat na jeden reduce (místo map, filter a reduce).
    return new Promise((resolve) => {
      this.logger.debug('Downloader#download');
      return resolve(
        // Výběr médií ke stažení.
        // TODO #84 Stahovat pouze užitečné soubory:
        // - Ideálně pouze z hlavního tasku a z jeho médií.
        // - Možnost použít ScheduleManager#findTask().
        // - Zohlednit jak se bude stahovat obsah s více plánovanými úkoly.
        contentInfo.mediumArray
          .map((medium) => ({
            medium,
            remoteUrl: medium.url,
            viewerType: this.viewerRegistry.find(medium.type)
          }))
          .filter(
            ({ remoteUrl, viewerType }) =>
              // Musí být definované URL média na serveru, ze kterého se stáhne.
              !isNil(remoteUrl) &&
              // Médium musí mít typ, který vyžaduje stažení.
              viewerType &&
              viewerType.isOfflineable()
          )
          // Sériové stahování souborů pro jednotlivá média.
          .reduce(
            (accumulator, { medium, remoteUrl, viewerType }) =>
              accumulator.then((assetArray) =>
                this.downloadAsset(contentInfo, medium, remoteUrl, viewerType)
                  // Navázání média na vytvořený soubor.
                  .then((asset) => {
                    // Příprava napojení média v novém obsahu na asset, který byl stažen.
                    // Napojení se aktivuje až v okamžiku vložení nového obsahu do hlavního datového skladu.
                    contentInfo.store.inject('medium', {
                      assetId: asset.id,
                      id: medium.id
                    });
                    // Úspěšně stažený asset se přidá do výstupu.
                    assetArray.push(asset);
                  })
                  // TODO #84 Zde by se mělo kontrolovat, jestli nebylo vyžádáno přerušení.
                  // Pokud něco selže, tak se přeskočí na další médium.
                  .catch((error) => {
                    if (error.name === ErrorName.NotFoundError) {
                      // Nenalezení jednotky nechceme logovat
                      return;
                    }

                    this.logger.error(error);
                  })
                  // Předání akumulovaných výsledků pro další médium.
                  // TODO Vrací se objekty, které jsou v primaryStore a ne v contentStore
                  .then(() => assetArray)
              ),
            Promise.resolve([])
          )
      );
    });
  }

  /**
   * Stáhne jeden soubor z nového obsahu.
   * @param {ContentInfo} contentInfo
   * @param {Medium} medium
   * @param {string} remoteUrl
   * @param {ViewerType} viewerType
   * @returns {Promise.<Asset>}
   */
  downloadAsset(contentInfo, medium, remoteUrl, viewerType) {
    return new Promise((resolve) => {
      const { defaultSize } = this;
      this.logger.debug(`Downloader#downloadAsset start ${remoteUrl}`);
      const cache = !viewerType.getCheckFile(medium);
      const checksum = medium.checksum;
      const urlObject = new URL(remoteUrl);
      const extract = viewerType.isExtractable();
      // Pokud existuje v localstorage, pak existuje i fyzicky. To je zarucene diky this.assetDownloader.restore()
      const asset = this.assetDownloader.findAsset(urlObject, { checksum });
      // TODO: nemohlo by se zde taky kontrolovat jestli existuje asset.path??
      if (!isNil(asset) && cache) {
        return resolve(asset);
      }

      // Soubor neni dostupny nebo je vypnute cachovani
      return resolve(
        FileSystemUtil.find(
          this.fileSystemManager,
          this.fileSystemUnitFinder,
          this.logicalUnitMapper
        ).then(async (logicalUnit) => {
          if (isNil(logicalUnit)) {
            const error = new Error('No unit found.');
            error.name = ErrorName.NotFoundError;
            throw error;
          }
          const logicalPath = await this.basePathResolver.resolve(logicalUnit);
          // Detekce dostatečného místa k uložení souboru.
          // Zatím se používá pouze výchozí hodnota z konfigurace.
          // Nezjišťuje se skutečná velikost souboru.
          // TODO udelat nejak jinak nez replace

          return (
            this.fileSystemSpaceDetector
              .detect(logicalUnit, defaultSize)
              .then(async (detected) => {
                // Pokud je dostatek místa, není třeba místo uvolňovat.
                if (detected) {
                  return;
                }
                await this.cleaner.clean(logicalPath, contentInfo);
                const detectedAfterClean =
                  await this.fileSystemSpaceDetector.detect(
                    logicalUnit,
                    defaultSize
                  );
                if (!detectedAfterClean) {
                  // Pokud ani po uvolnění není dostatek místa, vyhodí se chyba,
                  // která se zaloguje.
                  throw Error(`Unable to clean space for medium ${medium}.`);
                }
                // Uvolnil se dostatek místa, soubor lze stáhnout.
              })
              // Persistentní uložení souboru do hlavního datového skladu.
              .then(() =>
                this.assetDownloader.download(logicalPath, remoteUrl, {
                  checksum,
                  extract
                })
              )
          );
        })
      );
    }).finally(() => {
      this.logger.debug(`Downloader#downloadAsset end ${remoteUrl}`);
    });
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na požadavek na přepnutí obsahu z čističe obsahu.
   * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @private
   * @returns {undefined} Nic.
   */
  onContent(contentInfo) {
    this.emitter.emit('content', contentInfo);
  }

  /**
   * Reakce na chybu.
   * @param {Error} error Chybový objekt.
   * @private
   * @returns {undefined} Nic.
   */
  onError(error) {
    this.logger.error(error);
  }

  /**
   * Obnoví obsah datového zdroje s binárními soubory.
   * @returns {Promise.<Array.<Asset>>} Seznam obnovených assetů.
   */
  start() {
    return this.assetDownloader.checkAssets().catch((error) => {
      this.logger.error(error);
    });
  }
}

/**
 * Nový obsah zařízení k zobrazení.
 * Používá se pro přerušení přehrávání aktuálního obsahu, aby mohl být vyčištěn.
 * @event Downloader#content
 * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
 */

export default Downloader;
